/** Toolbar and bottom bar. Spec §4.1 and §4.2. */
import type { CSSProperties } from 'react';

/** uikit_layout_toolbar.xml + uikit_layout_header.xml.
 *
 * Transparent over the image, fading to white as you scroll. The lerp is
 * `animateAlphaOnScroll`: alpha 0 -> 1 over `312dp - toolbarHeight` of scroll. The back
 * pill and the title interpolate with it — white glyph on a black-80 pill at the top,
 * #626262 on white once scrolled.
 */
export function Toolbar({
  title,
  statusBarDp,
  alpha,
}: {
  title: string;
  statusBarDp: number;
  alpha: number;
}) {
  const a = Math.max(0, Math.min(1, alpha));
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        background: `rgb(255 255 255 / ${a})`,
        boxShadow: a > 0 ? `0 ${8 * a}px ${8 * a}px rgb(0 0 0 / ${0.08 * a})` : 'none',
      }}
    >
      <div style={{ height: statusBarDp }} />
      <div style={{ height: 56, display: 'flex', alignItems: 'center', position: 'relative' }}>
        <div
          aria-label="Back"
          style={{
            width: 34,
            height: 34,
            marginLeft: 11,
            borderRadius: 17,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // bg_button_rounded lerps black-80 -> transparent as the bar fades in
            background: `rgb(0 0 0 / ${0.8 * (1 - a)})`,
            color: a < 0.5 ? 'var(--and-color-white)' : 'var(--and-color-color-dark-medium)',
            fontSize: 18,
          }}
        >
          ←
        </div>
        <div
          className="and-type-legacy-headline2"
          style={{
            marginLeft: 16,
            marginRight: 16,
            flex: 1,
            minWidth: 0,
            opacity: a,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

/** uikit_layout_bottom_bar.xml — minHeight 56dp, over an 8dp gradient strip. */
export function BottomBar({
  title,
  caption,
  buttonText,
  buttonPlacement,
  navBarDp,
}: {
  title: string | null;
  caption: string | null;
  buttonText: string | null;
  buttonPlacement: 'end' | 'center';
  navBarDp: number;
}) {
  const button = buttonText ? (
    <div style={buttonStyle}>{buttonText}</div>
  ) : null;

  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2 }}>
      {/* uikit_bottom_view_shadow: #00000000 -> #1a626262, 8dp */}
      <div style={{ height: 8, background: 'linear-gradient(to bottom, rgb(0 0 0 / 0), rgb(98 98 98 / 0.1))' }} />
      <div
        style={{
          minHeight: 56,
          background: 'var(--and-color-white)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: buttonPlacement === 'center' && !title && !caption ? 'center' : 'space-between',
          paddingBottom: navBarDp,
        }}
      >
        {(title || caption) && (
          <div style={{ marginLeft: 16, minWidth: 0 }}>
            {title && (
              <div className="and-type-legacy-subtitle2" style={{ marginTop: 8 }}>
                {title}
              </div>
            )}
            {caption && (
              <div className="and-type-caption-dark-medium" style={{ marginBottom: 8 }}>
                {caption}
              </div>
            )}
          </div>
        )}
        {button && <div style={{ marginRight: buttonPlacement === 'end' ? 16 : 0 }}>{button}</div>}
      </div>
    </div>
  );
}

/** UiKitButton PRIMARY/REGULAR: padding 16/10, radius 12dp, blue fill, white 16sp Medium. */
const buttonStyle: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 12,
  background: 'var(--and-color-blue)',
  color: 'var(--and-color-white)',
  fontSize: 16,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
